import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatFileSize } from '@wundo/shared/fileSize.ts';
import {
  Button,
  ImageUploadButton,
  ImageUploadDialog,
  OrganisationLogo,
  SettingsRow,
  SettingsSection,
} from '@wundo/ui';
import {
  MAX_ORGANISATION_LOGO_BYTES,
  ORGANISATION_LOGO_TYPES,
  fetchOrganisationLogo,
  getOrganisation,
  removeOrganisationLogo,
  uploadOrganisationLogo,
  type LogoFetchOutcome,
  type OrganisationDetail,
} from '@/lib/organisations.ts';

const MAX_SIZE = formatFileSize(MAX_ORGANISATION_LOGO_BYTES);

export interface OrganisationLogoSectionProps {
  organisationId: string;
}

/**
 * The organisation's logo, changed the way a profile photo is: click it to
 * upload or remove one. Looking it up again from the domain is the quiet
 * button beside it.
 *
 * Its own query key, so a logo change never refetches the record the form
 * below is editing, which would reset unsaved fields.
 */
export function OrganisationLogoSection({
  organisationId,
}: OrganisationLogoSectionProps) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<LogoFetchOutcome | null>(null);

  const { data: organisation } = useQuery({
    queryKey: ['organisation', organisationId, 'logo'],
    queryFn: () => getOrganisation(organisationId),
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['organisation', organisationId, 'logo'],
    });
    await queryClient.invalidateQueries({ queryKey: ['organisations'] });
  };

  const upload = useMutation({
    mutationFn: (file: File) => uploadOrganisationLogo(organisationId, file),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => removeOrganisationLogo(organisationId),
    onSuccess: refresh,
  });

  const lookup = useMutation({
    mutationFn: () => fetchOrganisationLogo(organisationId),
    onMutate: () => setOutcome(null),
    onSuccess: async (result) => {
      setOutcome(result);
      await refresh();
    },
  });

  if (!organisation) return null;

  const hasUpload = organisation.logo_source === 'staff';

  return (
    <>
      <SettingsSection>
        <SettingsRow
          label='Logo'
          description={
            lookup.isError
              ? 'Looking up the logo failed. Try again in a moment.'
              : describe(organisation, outcome)
          }
        >
          <div className='flex items-center gap-3'>
            <Button
              size='sm'
              variant='ghost'
              loading={lookup.isPending}
              disabled={hasUpload || !organisation.primary_domain}
              onClick={() => lookup.mutate()}
            >
              Fetch logo
            </Button>

            <ImageUploadButton
              shape='square'
              aria-label={organisation.logo_url ? 'Change logo' : 'Add logo'}
              onClick={() => setOpen(true)}
            >
              <OrganisationLogo
                className='size-full'
                src={organisation.logo_url}
                name={organisation.name}
              />
            </ImageUploadButton>
          </div>
        </SettingsRow>
      </SettingsSection>

      {open && (
        <ImageUploadDialog
          title='Logo'
          closeLabel='Close'
          preview={
            <OrganisationLogo
              size='xl'
              src={organisation.logo_url}
              name={organisation.name}
            />
          }
          hint={`A square image works best. JPG, PNG or WebP, up to ${MAX_SIZE}.`}
          acceptTypes={ORGANISATION_LOGO_TYPES}
          maxBytes={MAX_ORGANISATION_LOGO_BYTES}
          tooLargeMessage={`That image is too large. Choose one of up to ${MAX_SIZE}.`}
          wrongTypeMessage='Choose a JPG, PNG or WebP file.'
          failedMessage={
            upload.isError || remove.isError
              ? 'That did not work. Try again in a moment.'
              : null
          }
          uploadLabel={organisation.logo_url ? 'Change logo' : 'Add logo'}
          uploading={upload.isPending}
          onUpload={(file) => {
            setOutcome(null);
            upload.mutate(file);
          }}
          removeLabel='Remove'
          removing={remove.isPending}
          onRemove={
            organisation.logo_url
              ? () => {
                  setOutcome(null);
                  remove.mutate();
                }
              : undefined
          }
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function describe(organisation: OrganisationDetail, outcome: LogoFetchOutcome | null) {
  const domain = organisation.primary_domain;

  if (outcome === 'not_found') return `No usable logo found for ${domain}.`;
  if (organisation.logo_source === 'staff') {
    return 'Uploaded here. Remove it to look the logo up from the domain again.';
  }
  if (!domain) return 'Fill in the domain below to look the logo up automatically.';
  if (organisation.logo_source === 'detected') {
    return `Found automatically for ${domain}.`;
  }

  return `No logo yet. Fetch logo looks it up for ${domain}.`;
}
